<template>
  <div class="user-page">

    <!-- 搜索框（右上） -->
    <div class="search-bar">
      <el-input
          v-model="searchText"
          placeholder="输入用户名搜索"
          clearable
          @keyup.enter="onSearch"
          class="search-input"
      />
      <el-button type="primary" class="search-btn" @click="onSearch">
        搜索
      </el-button>
    </div>

    <!-- 骨架屏 -->
    <el-skeleton
        v-if="loading"
        :rows="6"
        animated
        class="skeleton-box"
    />

    <!-- 表格 -->
    <el-table
        v-else
        :data="users"
        border
        class="user-table"
        stripe
        style="width:100%;"
    >

      <el-table-column prop="id" label="ID" min-width="90" show-overflow-tooltip />

      <el-table-column prop="openid" label="openID" min-width="200" show-overflow-tooltip />

      <el-table-column prop="nickname" label="用户名" min-width="130" show-overflow-tooltip />

      <!-- avatar 图片 -->
      <el-table-column label="头像" min-width="120">
        <template #default="{ row }">
          <div class="avatar-wrap">
            <img :src="row.avatar" class="avatar-img" @error="onAvatarError" />
          </div>
        </template>
      </el-table-column>

      <el-table-column prop="member_name" label="会员名" min-width="150" show-overflow-tooltip />

      <el-table-column prop="phone" label="手机号" min-width="140" show-overflow-tooltip />

      <el-table-column prop="is_vip" label="是否会员" min-width="100">
        <template #default="{ row }">
          <el-tag :type="row.is_vip ? 'success' : 'info'">
            {{ row.is_vip ? "是" : "否" }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="vip_expire_at" label="VIP到期时间" min-width="170" show-overflow-tooltip>
        <template #default="{ row }">
          {{ formatDate(row.vip_expire_at) }}
        </template>
      </el-table-column>

      <el-table-column prop="status" label="账号状态" min-width="100">
        <template #default="{ row }">
          <el-tag :type="row.status === 1 ? 'success' : 'danger'">
            {{ row.status === 1 ? "正常" : "封禁" }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="用户备注" min-width="110">
        <template #default="{ row }">
          {{ row.description || '' }}
        </template>
      </el-table-column>

      <!-- 创建时间 -->
      <el-table-column label="创建时间" min-width="170" show-overflow-tooltip>
        <template #default="{ row }">
          {{ formatDate(row.created_at) }}
        </template>
      </el-table-column>

      <!-- 更新时间 -->
      <el-table-column label="更新时间" min-width="170" show-overflow-tooltip>
        <template #default="{ row }">
          {{ formatDate(row.updated_at) }}
        </template>
      </el-table-column>

      <el-table-column label="操作" fixed="right" min-width="150">
        <template #default="{ row }">
          <el-button type="primary" size="small" text @click="openEdit(row)">编辑</el-button>
          <el-button type="danger" size="small" text @click="deleteUser(row.id)">删除</el-button>
        </template>
      </el-table-column>

    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
          background
          layout="prev, pager, next"
          :total="total"
          :page-size="20"
          :current-page="page"
          @current-change="pageChange"
      />
    </div>

    <!-- 编辑弹窗 -->
    <el-dialog
        v-model="editVisible"
        title="编辑用户"
        width="520px"
        class="edit-dialog"
    >
      <el-form :model="editForm" label-width="100px">
        <el-form-item label="用户名">
          <el-input v-model="editForm.nickname" />
        </el-form-item>

        <el-form-item label="会员名">
          <el-input v-model="editForm.member_name" />
        </el-form-item>

        <el-form-item label="手机号">
          <el-input v-model="editForm.phone" />
        </el-form-item>

        <el-form-item label="账号状态">
          <el-select v-model="editForm.status">
            <el-option label="正常" :value="1" />
            <el-option label="封禁" :value="0" />
          </el-select>
        </el-form-item>

        <el-form-item label="备注">
          <el-input type="textarea" v-model="editForm.description" rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="editVisible = false">取消</el-button>
        <el-button type="primary" @click="submitEdit">保存</el-button>
      </template>
    </el-dialog>

  </div>
</template>

<script setup>
import { ref } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import api from '@/services/api';

const searchText = ref("");
const users = ref([]);
const total = ref(0);
const page = ref(1);
const loading = ref(false);

/* ---- 时间格式化 ---- */
const formatDate = (t) => {
  if (!t) return "";
  const d = new Date(t);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

/* ---- 通用请求封装 ---- */
async function safeRequest(fn, defaultValue) {
  try {
    return await fn();
  } catch (err) {
    console.warn("请求失败:", err.message);
    return defaultValue;
  }
}

/* ---- 加载列表 ---- */
const fetchUsers = async () => {
  loading.value = true;

  const rawRes = await safeRequest(
      () =>
          api.get("/users", {
            params: {
              page: page.value,
              size: 20,
              keyword: searchText.value,
            },
          }),
      { data: [], total: 0 }
  );

  let payload = rawRes?.data?.data ? rawRes.data : rawRes;
  users.value = Array.isArray(payload.data) ? payload.data : [];
  total.value = Number(payload.total) || 0;

  loading.value = false;
};

const onSearch = () => {
  page.value = 1;
  fetchUsers();
};

const pageChange = (p) => {
  page.value = p;
  fetchUsers();
};

const isExpired = (t) => {
  if (!t) return true;
  return new Date(t) < new Date();
};

const deleteUser = (id) => {
  ElMessageBox.confirm("确定删除此用户？", "提示", {
    type: "warning",
  }).then(async () => {
    await safeRequest(() => api.delete(`/users/${id}`));
    ElMessage.success("删除成功");
    fetchUsers();
  });
};

const editVisible = ref(false);
const editForm = ref({});

const openEdit = (row) => {
  editForm.value = { ...row };
  editVisible.value = true;
};

const submitEdit = async () => {
  await safeRequest(() =>
      api.put(`/users/${editForm.value.id}`, editForm.value)
  );
  ElMessage.success("保存成功");
  editVisible.value = false;
  fetchUsers();
};

/* 头像错误占位 */
const onAvatarError = (e) => {
  e.target.src =
      "data:image/svg+xml;utf8,<svg width='60' height='60' xmlns='http://www.w3.org/2000/svg'><rect width='100%' height='100%' fill='%23EEE'/><text x='50%' y='50%' font-size='10' fill='%999' text-anchor='middle' dominant-baseline='middle'>avatar</text></svg>";
};

fetchUsers();
</script>

<style scoped>
.user-page {
  padding: 25px;
  background: #fafafa;
}

/* 表格强制固定布局 */
.user-table {
  table-layout: fixed !important;
}

/* 搜索框右上 */
.search-bar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 14px;
  gap: 10px;
}

.search-input {
  width: 300px;
  border-radius: 10px;
}

.search-btn {
  border-radius: 10px;
}

/* 头像样式 */
.avatar-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-img {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  object-fit: cover;
  border: 1px solid #eee;
  background: #fff;
}

/* 分页 */
.pagination {
  margin-top: 18px;
  display: flex;
  justify-content: center;
}

/* 弹窗美化 */
.edit-dialog :deep(.el-dialog) {
  border-radius: 12px !important;
}
</style>
